import { useMemo, useState } from "react";
import { Image, ScrollView, Text, View } from "react-native";
import {
  Activity,
  AlertCircle,
  Beaker,
  Bed,
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  FileText,
  Heart,
  Info,
  MapPin,
  Moon,
  TestTube2,
  Timer,
  Wrench,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  getCollectionLabel,
  getRelatedBlocks,
  type ContentBlock,
  type ProcedureBody,
  type TestBody,
} from "@src/packages/content";
import { useLocale, useTranslations } from "@src/packages/i18n";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import {
  CardSurface,
  CloseButton,
  DisclaimerNote,
  FactGrid,
  MoonSegmentedControl,
  SectionTitle,
  type MoonSegmentedOption,
} from "@louise/moon-ui";
import FactSheetHeader, {
  type FactSheetTagItem,
} from "../../components/FactSheetHeader";
import EvidenceLevelModal from "../../components/EvidenceLevelModal";
import EvidenceSourceRows from "../../components/EvidenceSourceRows";
import AccessGridCard, {
  AccessGridCardPill,
} from "../../components/access/AccessGridCard";
import { getTestIllustration } from "../../components/access/testIllustrations";
import { getProcedureIllustration } from "../../components/access/procedureIllustrations";
import { testQuickFactsAsGlanceFacts } from "../../components/testGlanceFacts";
import { EMBER } from "../../../onboarding/theme";

type Props = NativeStackScreenProps<
  AccessStackParamList,
  "CanonicalTestFactsheet"
>;
type FactsheetTab = "overview" | "values" | "biology" | "testing";
type ProcedureQuickFact = { label: string; value: string; icon: LucideIcon };

function getTestBlock(id: string): ContentBlock<"test"> | null {
  const candidates = id.startsWith("test-") ? [id] : [id, `test-${id}`];
  return (
    (getBlocksByKind("test") as ContentBlock<"test">[]).find((block) =>
      candidates.includes(String(block.id)),
    ) ?? null
  );
}

function getProcedureBlock(id: string): ContentBlock<"procedure"> | null {
  const normalizedId = id === "procedure-hsg" ? "proc-hsg" : id;
  const candidates = normalizedId.startsWith("proc-")
    ? [normalizedId]
    : [normalizedId, `proc-${normalizedId}`];
  return (
    (getBlocksByKind("procedure") as ContentBlock<"procedure">[]).find(
      (block) => candidates.includes(String(block.id)),
    ) ?? null
  );
}

function getCategoryLabel(
  category: TestBody["category"],
  t: ReturnType<typeof useTranslations>,
): string | undefined {
  switch (category) {
    case "blood-marker":
      return t("learn.test_detail.category_blood_marker");
    case "imaging":
      return t("learn.test_detail.category_imaging");
    case "genetic":
      return t("learn.test_detail.category_genetic");
    case "semen":
      return t("learn.test_detail.category_semen");
    case "monitoring":
      return t("learn.test_detail.category_monitoring");
    case "biopsy":
      return t("learn.test_detail.category_biopsy");
    default:
      return undefined;
  }
}

function BodyCopy({ children }: { children: string }) {
  return (
    <Text
      className="font-dm-sans"
      style={{ fontSize: 14, lineHeight: 22, color: EMBER.colors.textLight }}
    >
      {children}
    </Text>
  );
}

function splitProcedureSteps(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((step) => step.trim())
    .filter(Boolean);
}

function BulletList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 8 }}>
      {items.map((item, index) => (
        <Text
          key={`${item.slice(0, 24)}-${index}`}
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 20,
            color: EMBER.colors.textLight,
          }}
        >
          • {item}
        </Text>
      ))}
    </View>
  );
}

function ResultTypeList({
  items,
}: {
  items: NonNullable<TestBody["resultTypes"]>;
}) {
  return (
    <View style={{ gap: 16 }}>
      {items.map((item) => (
        <View key={item.label} style={{ gap: 5 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 18,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {item.label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
            }}
          >
            {item.meaning}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textMid,
            }}
          >
            {item.whatToExpect}
          </Text>
        </View>
      ))}
    </View>
  );
}

function FactsheetInfoList({
  items,
  mode,
}: {
  items: string[];
  mode: "steps" | "prepare" | "expect";
}) {
  const MarkerIcon = mode === "prepare" ? ClipboardCheck : CalendarDays;

  return (
    <View style={{ gap: 12 }}>
      {items.map((item, index) => (
        <View
          key={`${item.slice(0, 24)}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <View style={{ width: 16, alignItems: "center", paddingTop: 2 }}>
            {mode === "steps" ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 15,
                  fontWeight: "700",
                  color: EMBER.colors.textLight,
                }}
              >
                {index + 1}
              </Text>
            ) : (
              <MarkerIcon
                size={13}
                color={EMBER.colors.textLight}
                strokeWidth={1.8}
              />
            )}
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              lineHeight: 19,
              color: EMBER.colors.textLight,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function MarkerTiming({
  items,
}: {
  items: NonNullable<TestBody["markerTiming"]>;
}) {
  return (
    <View style={{ gap: 14 }}>
      {items.map((item, index) => (
        <View
          key={`${item.marker}-${index}`}
          style={{ flexDirection: "row", gap: 12 }}
        >
          <CalendarDays
            size={17}
            color={EMBER.colors.textMid}
            strokeWidth={1.7}
          />
          <View style={{ flex: 1, gap: 4 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "700",
                color: EMBER.colors.textMid,
              }}
            >
              {item.marker}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.textLight,
              }}
            >
              {item.timing}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textFaint,
              }}
            >
              {item.reason}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

function AccessSummary({
  test,
  imageSource,
  t,
}: {
  test: ContentBlock<"test">;
  imageSource: ReturnType<typeof getTestIllustration>;
  t: ReturnType<typeof useTranslations>;
}) {
  const body = test.body;
  const cost = body.costRange
    ? `$${body.costRange.low}–$${body.costRange.high}`
    : undefined;

  return (
    <CardSurface
      variant="panel"
      style={{
        flexDirection: "row",
        gap: 14,
        alignItems: "center",
        padding: 14,
      }}
    >
      <Image
        source={imageSource}
        style={{ width: 72, height: 72 }}
        resizeMode="contain"
      />
      <View style={{ flex: 1, gap: 7 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 15, fontWeight: "700", color: EMBER.colors.text }}
        >
          {getCollectionLabel(body)}
        </Text>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {body.atHome ? (
            <AccessGridCardPill label={t("learn.test_detail.badge_at_home")} />
          ) : null}
          {body.clinicRequired ? (
            <AccessGridCardPill
              label={t("learn.test_detail.badge_clinic_visit")}
            />
          ) : null}
          {body.turnaroundTime ? (
            <AccessGridCardPill label={body.turnaroundTime} />
          ) : null}
          {cost ? <AccessGridCardPill label={cost} /> : null}
        </View>
      </View>
    </CardSurface>
  );
}

function getRelatedTestingOptions(
  test: ContentBlock<"test">,
): ContentBlock<"test">[] {
  const related = getRelatedBlocks(test.id, "related-to")
    .filter(
      ({ relationship, block }) =>
        relationship.label === "Sold as" && block.kind === "test",
    )
    .map(({ block }) => block)
    .filter((block): block is ContentBlock<"test"> => block.kind === "test")
    .filter((block) => block.body.isCommercialProductProfile);

  return Array.from(
    new Map(related.map((block) => [block.id, block])).values(),
  );
}

function getRelatedProcedureTests(
  procedure: ContentBlock<"procedure">,
): ContentBlock<"test">[] {
  const related = getRelatedBlocks(procedure.id, "related-to")
    .filter(
      ({ relationship, block }) =>
        (relationship.label === "Can lead to" ||
          relationship.label === "Canonical test" ||
          relationship.label === "Procedure used for" ||
          relationship.label === "Sample collected for") &&
        block.kind === "test",
    )
    .map(({ block }) => block)
    .filter((block): block is ContentBlock<"test"> => block.kind === "test");

  return Array.from(
    new Map(related.map((block) => [block.id, block])).values(),
  );
}

function RelatedTestCard({
  test,
  onPress,
}: {
  test: ContentBlock<"test">;
  onPress: () => void;
}) {
  const cost = test.body.costRange
    ? `$${test.body.costRange.low}–$${test.body.costRange.high}`
    : undefined;

  return (
    <View style={{ width: "48%" }}>
      <AccessGridCard
        title={test.title}
        description={test.subtitle}
        imageSource={getTestIllustration({
          testId: String(test.id),
          category: test.body.category,
        })}
        imageStyle={{ width: 66, height: 84 }}
        onPress={onPress}
        surface="panel"
        tags={
          <>
            <AccessGridCardPill label={getCollectionLabel(test.body)} />
            {test.body.turnaroundTime ? (
              <AccessGridCardPill label={test.body.turnaroundTime} />
            ) : null}
            {cost ? <AccessGridCardPill label={cost} /> : null}
          </>
        }
      />
    </View>
  );
}

export default function CanonicalTestFactsheetScreen({
  navigation,
  route,
}: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const [activeTab, setActiveTab] = useState<FactsheetTab>(
    route.params.initialTab ?? "overview",
  );
  const [evidenceLevel, setEvidenceLevel] = useState<string | null>(null);
  const isProcedure = Boolean(route.params.procedureId);
  const handleBack = useSmartBack({
    returnTab: route.params.returnTab,
    fallbackTab: isProcedure ? "Learn" : "Access",
    fallbackScreen: isProcedure ? "Procedures" : "Testing",
  });
  const test = useMemo(() => {
    void locale;
    return route.params.testId ? getTestBlock(route.params.testId) : null;
  }, [route.params.testId, locale]);
  const procedure = useMemo(() => {
    void locale;
    return route.params.procedureId
      ? getProcedureBlock(route.params.procedureId)
      : null;
  }, [route.params.procedureId, locale]);
  const testingOptions = useMemo(
    () => (test ? getRelatedTestingOptions(test) : []),
    [test],
  );
  const procedureTestingOptions = useMemo(
    () => (procedure ? getRelatedProcedureTests(procedure) : []),
    [procedure],
  );

  if (!test && !procedure) return null;

  const body = test?.body;
  const procedureBody = procedure?.body;
  const localizedProcedure = procedureBody
    ? (() => {
        const localizedBody = locale === "fr" ? procedureBody.frBody : null;
        return {
          procedureName:
            localizedBody?.procedureName ?? procedureBody.procedureName,
          description: localizedBody?.description ?? procedureBody.description,
          howItWorks: localizedBody?.howItWorks ?? procedureBody.howItWorks,
          duration: localizedBody?.duration ?? procedureBody.duration,
          recoveryTime:
            localizedBody?.recoveryTime ?? procedureBody.recoveryTime,
          painAndComfort:
            localizedBody?.painAndComfort ??
            (locale === "fr" ? [] : (procedureBody.painAndComfort ?? [])),
          prepSummary: localizedBody?.prepSummary ?? procedureBody.prepSummary,
          risks:
            localizedBody?.risks ??
            (locale === "fr" ? [] : (procedureBody.risks ?? [])),
          prepInstructions:
            localizedBody?.prepInstructions ??
            (locale === "fr" ? [] : (procedureBody.prepInstructions ?? [])),
          whatToExpect:
            localizedBody?.whatToExpect ??
            (locale === "fr" ? [] : (procedureBody.whatToExpect ?? [])),
          results:
            localizedBody?.results ??
            (locale === "fr" ? [] : (procedureBody.results ?? [])),
        } satisfies Partial<ProcedureBody>;
      })()
    : null;
  const testImageSource =
    test && body
      ? getTestIllustration({
          testId: String(test.id),
          category: body.category,
        })
      : undefined;
  const imageSource = isProcedure
    ? getProcedureIllustration({
        procedureId: String(procedure?.id),
        category: procedureBody?.category,
      })
    : testImageSource;
  const categoryLabel = body ? getCategoryLabel(body.category, t) : undefined;
  const howItWorksSteps = body
    ? (body.howItWorksSteps ??
      body.howItWorks
        .split(/(?<=[.!?])\s+/)
        .map((step) => step.trim())
        .filter(Boolean))
    : [];
  const procedurePrepSummary = localizedProcedure
    ? (localizedProcedure.prepSummary ?? localizedProcedure.prepInstructions[0])
    : undefined;
  const procedureQuickFacts: ProcedureQuickFact[] = localizedProcedure
    ? [
        ...(localizedProcedure.duration
          ? [
              {
                label: t("learn.test_detail.quick_fact_time"),
                icon: Timer,
                value: localizedProcedure.duration,
              },
            ]
          : []),
        ...(localizedProcedure.painAndComfort[0]
          ? [
              {
                label: t("learn.test_detail.quick_fact_pain"),
                icon: Activity,
                value: localizedProcedure.painAndComfort[0],
              },
            ]
          : []),
        ...(procedurePrepSummary
          ? [
              {
                label: t("learn.test_detail.quick_fact_prep"),
                icon: ClipboardCheck,
                value: procedurePrepSummary,
              },
            ]
          : []),
        ...(procedureBody?.anesthesia
          ? [
              {
                label: t("learn.test_detail.quick_fact_anesthesia"),
                icon: Moon,
                value:
                  procedureBody.anesthesia === "none"
                    ? t("procedure_detail.anesthesia_none")
                    : procedureBody.anesthesia === "local"
                      ? t("procedure_detail.anesthesia_local")
                      : procedureBody.anesthesia === "sedation"
                        ? t("procedure_detail.anesthesia_sedation")
                        : t("procedure_detail.anesthesia_general"),
              },
            ]
          : []),
        ...(localizedProcedure.recoveryTime
          ? [
              {
                label: t("procedure_detail.recovery_label"),
                icon: Bed,
                value: localizedProcedure.recoveryTime,
              },
            ]
          : []),
      ]
    : [];
  const procedureHowItWorksSteps = localizedProcedure
    ? splitProcedureSteps(localizedProcedure.howItWorks)
    : [];
  const valuesConsiderations = body
    ? [
        ...(body.expectations?.shouldNotExpect ?? []),
        ...(body.considerations ?? []),
      ]
    : [];
  const hasValuesContent = Boolean(
    body?.resultTypes?.length || valuesConsiderations.length,
  );
  const tabs: MoonSegmentedOption<FactsheetTab>[] = [
    {
      value: "overview",
      label: t("care.lab_result.tab_overview"),
      shortLabel: t("care.lab_result.tab_overview_short"),
      icon: Info,
    },
    ...(isProcedure
      ? [
          {
            value: "biology" as const,
            label: t("procedure_detail.tab_procedure"),
            shortLabel: t("procedure_detail.tab_procedure_short"),
            icon: Wrench,
          },
        ]
      : hasValuesContent
        ? [
            {
              value: "values" as const,
              label: t("care.lab_result.tab_values"),
              shortLabel: t("care.lab_result.tab_values_short"),
              icon: FileText,
            },
          ]
        : []),
    ...(isProcedure
      ? []
      : [
          {
            value: "biology" as const,
            label: t("care.lab_result.tab_biology"),
            shortLabel: t("care.lab_result.tab_biology_short"),
            icon: Beaker,
          },
        ]),
    {
      value: "testing",
      label: isProcedure
        ? t("procedure_detail.tab_access")
        : t("access.testing.comparison.get_tested"),
      shortLabel: isProcedure
        ? t("procedure_detail.tab_access_short")
        : t("access.testing.comparison.get_tested_short"),
      icon: isProcedure ? MapPin : TestTube2,
    },
  ];
  // The sheet's labels use the shared tags: what kind of test it is, and where
  // it is collected.
  const headerTags: FactSheetTagItem[] = [
    ...(!isProcedure && categoryLabel
      ? [{ kind: "category" as const, label: categoryLabel }]
      : []),
    ...(test && body
      ? [{ kind: "feature" as const, label: getCollectionLabel(body) }]
      : []),
  ];

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <CardSurface
          style={{ paddingHorizontal: 24, paddingTop: 24, paddingBottom: 32 }}
        >
          <View style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
            <CloseButton
              onPress={handleBack}
              accessibilityLabel={t(
                "learn.article_actions.close_accessibility",
              )}
            />
          </View>

          <FactSheetHeader
            title={
              procedure
                ? (localizedProcedure?.procedureName ?? procedure.title)
                : (test?.title ?? "")
            }
            subtitle={
              procedure
                ? locale === "fr"
                  ? procedureBody?.frSubtitle
                  : procedure.subtitle
                : test?.subtitle
            }
            imageSource={imageSource}
            imageSize={isProcedure ? 128 : 86}
            tags={headerTags}
          />

          <MoonSegmentedControl
            active={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {!isProcedure && activeTab === "overview" && body ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Heart}>
                  {t("learn.test_detail.section_what_it_measures")}
                </SectionTitle>
                <BodyCopy>{body.whatItMeasures}</BodyCopy>
              </View>

              {body.quickFacts?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Info}>
                    {t("learn.test_detail.section_at_a_glance")}
                  </SectionTitle>
                  <FactGrid
                    facts={testQuickFactsAsGlanceFacts(t, body.quickFacts)}
                  />
                </View>
              ) : null}

              {body.headlineStat ? (
                <CardSurface variant="panel" style={{ padding: 14 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      lineHeight: 19,
                      color: EMBER.colors.textMid,
                    }}
                  >
                    {body.headlineStat}
                  </Text>
                </CardSurface>
              ) : null}

              {body.expectations?.canExpect.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={ClipboardCheck}>
                    {t("learn.test_detail.section_what_to_expect")}
                  </SectionTitle>
                  <BulletList items={body.expectations.canExpect} />
                </View>
              ) : null}

              {body.bestFor?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Heart}>
                    {t("learn.test_detail.section_best_for")}
                  </SectionTitle>
                  <BulletList items={body.bestFor} />
                </View>
              ) : null}
            </View>
          ) : null}

          {isProcedure && activeTab === "overview" && localizedProcedure ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Heart}>
                  {t("procedure_detail.section_procedure_purpose")}
                </SectionTitle>
                <BodyCopy>{localizedProcedure.description}</BodyCopy>
              </View>

              <View style={{ gap: 10 }}>
                <SectionTitle icon={Info}>
                  {t("access.testing.comparison.at_a_glance")}
                </SectionTitle>
                <FactGrid
                  facts={procedureQuickFacts.map((fact) => ({
                    ...fact,
                    key: fact.label,
                  }))}
                />
              </View>
            </View>
          ) : null}

          {!isProcedure && activeTab === "values" && body ? (
            <View style={{ marginTop: 24, gap: 20 }}>
              {body.resultTypes?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={FileText}>
                    {t("learn.test_detail.section_possible_results")}
                  </SectionTitle>
                  <ResultTypeList items={body.resultTypes} />
                </View>
              ) : null}

              {valuesConsiderations.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("learn.test_detail.section_considerations")}
                  </SectionTitle>
                  <BulletList items={valuesConsiderations} />
                </View>
              ) : null}
            </View>
          ) : null}

          {!isProcedure && activeTab === "biology" && body ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Wrench}>
                  {t("learn.test_detail.section_how_it_works")}
                </SectionTitle>
                <BodyCopy>{body.howItWorks}</BodyCopy>
                {howItWorksSteps.length > 1 ? (
                  <FactsheetInfoList items={howItWorksSteps} mode="steps" />
                ) : null}
              </View>

              {body.cycleDay ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("learn.test_detail.section_when_to_take")}
                  </SectionTitle>
                  <BodyCopy>{body.cycleDay}</BodyCopy>
                </View>
              ) : null}

              {body.markerTiming?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("learn.test_detail.section_marker_timing")}
                  </SectionTitle>
                  <MarkerTiming items={body.markerTiming} />
                </View>
              ) : null}

              {test.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("learn.test_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={test.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {isProcedure && activeTab === "biology" && localizedProcedure ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Wrench}>
                  {t("procedure_detail.section_how_it_works")}
                </SectionTitle>
                {procedureHowItWorksSteps.length > 1 ? (
                  <FactsheetInfoList
                    items={procedureHowItWorksSteps}
                    mode="steps"
                  />
                ) : (
                  <BodyCopy>{localizedProcedure.howItWorks}</BodyCopy>
                )}
              </View>

              {localizedProcedure.prepInstructions.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={ClipboardCheck}>
                    {t("procedure_detail.section_how_to_prepare")}
                  </SectionTitle>
                  <FactsheetInfoList
                    items={localizedProcedure.prepInstructions}
                    mode="prepare"
                  />
                </View>
              ) : null}

              {localizedProcedure.whatToExpect.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("procedure_detail.section_what_to_expect")}
                  </SectionTitle>
                  <FactsheetInfoList
                    items={localizedProcedure.whatToExpect}
                    mode="expect"
                  />
                </View>
              ) : null}

              {localizedProcedure.painAndComfort.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Heart}>
                    {t("procedure_detail.section_pain_and_comfort")}
                  </SectionTitle>
                  <BulletList items={localizedProcedure.painAndComfort} />
                </View>
              ) : null}

              {localizedProcedure.risks.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("procedure_detail.section_risks")}
                  </SectionTitle>
                  <BulletList items={localizedProcedure.risks} />
                </View>
              ) : null}

              {localizedProcedure.results.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={FileText}>
                    {t("procedure_detail.section_results")}
                  </SectionTitle>
                  <View style={{ gap: 10 }}>
                    {localizedProcedure.results.map((result) => (
                      <View key={result.label} style={{ gap: 4 }}>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "700",
                            color: EMBER.colors.textMid,
                          }}
                        >
                          {result.label}
                        </Text>
                        <BodyCopy>{result.detail}</BodyCopy>
                      </View>
                    ))}
                  </View>
                </View>
              ) : null}

              {procedure?.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("procedure_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={procedure.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "testing" &&
          !isProcedure &&
          test &&
          body &&
          testImageSource ? (
            <View style={{ marginTop: 24, gap: 18 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={MapPin}>
                  {t("learn.test_detail.section_available_as")}
                </SectionTitle>
                <AccessSummary
                  test={test}
                  imageSource={testImageSource}
                  t={t}
                />
              </View>

              {testingOptions.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={TestTube2}>
                    {t("learn.test_detail.section_sold_as")}
                  </SectionTitle>
                  <View
                    style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}
                  >
                    {testingOptions.map((option) => (
                      <RelatedTestCard
                        key={option.id}
                        test={option}
                        onPress={() =>
                          navigation.navigate("TestDetail", {
                            testId: String(option.id),
                            returnTab: route.params.returnTab,
                          })
                        }
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              <View style={{ gap: 10 }}>
                <SectionTitle icon={Info}>
                  {t("learn.test_detail.section_what_to_expect")}
                </SectionTitle>
                <BodyCopy>
                  {body.clinicRequired
                    ? t("learn.test_detail.clinic_access_description")
                    : body.atHome
                      ? t("learn.test_detail.at_home_access_description")
                      : t("learn.test_detail.care_team_access_description")}
                </BodyCopy>
              </View>
            </View>
          ) : null}

          {activeTab === "testing" && isProcedure ? (
            <View style={{ marginTop: 24, gap: 18 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={MapPin}>
                  {procedureTestingOptions.length
                    ? t("procedure_detail.section_related_tests")
                    : t("procedure_detail.section_access")}
                </SectionTitle>
                <BodyCopy>
                  {procedureTestingOptions.length
                    ? t("procedure_detail.related_tests_description")
                    : t("procedure_detail.access_description")}
                </BodyCopy>
              </View>

              {procedureTestingOptions.length ? (
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}
                >
                  {procedureTestingOptions.map((option) => (
                    <RelatedTestCard
                      key={option.id}
                      test={option}
                      onPress={() =>
                        navigation.navigate("TestDetail", {
                          testId: String(option.id),
                          returnTab: route.params.returnTab,
                        })
                      }
                    />
                  ))}
                </View>
              ) : null}
            </View>
          ) : null}

          <DisclaimerNote style={{ marginTop: 32 }}>
            {t("learn.disclaimer_medical_info")}
          </DisclaimerNote>
        </CardSurface>
      </ScrollView>
      <EvidenceLevelModal
        level={evidenceLevel}
        visible={evidenceLevel !== null}
        onClose={() => setEvidenceLevel(null)}
      />
    </GlassScreenLayout>
  );
}
